import React from 'react';
import { Wifi, Cpu, Gauge, ShieldCheck } from 'lucide-react';

/**
 * AtmosicIoT — Step II of the gated Partner Sutra.
 *
 * The Sovereign Infrastructure Specs section. Names the sensor stack
 * (Atmosic ATM33 BLE-energy-harvesting + custom bracket), explains what
 * it actually measures, and gives partners the integrity-first reason
 * the brand sits on continuous physical witness rather than a quarterly
 * audit.
 */

const SPECS = [
  {
    icon:  Cpu,
    label: 'Module',
    value: 'Atmosic ATM33 · BLE 5.3 SoC, energy-harvesting',
  },
  {
    icon:  Gauge,
    label: 'Measures',
    value: 'Volume (ml) · pour cadence · refill events · tilt',
  },
  {
    icon:  Wifi,
    label: 'Telemetry',
    value: 'BLE → property gateway → /api/twins/{bottle}/sensor',
  },
  {
    icon:  ShieldCheck,
    label: 'Integrity',
    value: 'Tamper-evident bracket · NTAG22x · signed event hash',
  },
];

const AtmosicIoT = ({ testId = 'partner-step-ii' }) => (
  <section
    id="atmosic-iot"
    data-testid={testId}
    className="px-6 md:px-12 py-24 md:py-32 bg-[#7C9CB1] text-[#F4F8F9] border-t border-[#90B0BF]"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14">
        <div className="md:col-span-7">
          <p
            data-testid="atmosic-label"
            className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/75 mb-6"
          >
            <span className="inline-block w-7 h-px bg-[#F4F8F9]/75 mr-3 align-middle" />
            Threshold II · Sovereign infrastructure
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F4F8F9] font-light leading-[1.05]">
            We do not <em className="text-[#F4F8F9]/80">declare</em>
            <br />
            our integrity.
            <br />
            We sense it.
          </h2>
        </div>
        <div className="md:col-span-5 md:pt-4">
          <p className="font-serif italic text-lg text-[#F4F8F9]/90 leading-relaxed max-w-md">
            Every Shreesavaa vessel ships with a low-power radio that is
            literally cheaper to keep on than to switch off. It listens to
            its own contents — volume, cadence, refill, tamper — and signs
            each event before it leaves the property gateway.
          </p>
          <p className="mt-5 font-light text-[#F4F8F9]/85 leading-relaxed max-w-md">
            Savitri, the Guardian Steward, is the agent that reads this
            stream and arranges your next Conscious Transition refill.
          </p>
        </div>
      </div>

      <ul className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {SPECS.map((s) => {
          const Icon = s.icon;
          return (
            <li
              key={s.label}
              data-testid={`atmosic-spec-${s.label.toLowerCase()}`}
              className="relative border border-[#F4F8F9]/30 p-7 bg-[#7C9CB1]"
            >
              <span
                aria-hidden
                className="absolute top-0 left-7 right-7 h-px"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, #D4AF5A 35%, #D4AF5A 65%, transparent)',
                }}
              />
              <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70 mb-4">
                <Icon size={13} strokeWidth={1.5} /> {s.label}
              </p>
              <p className="font-serif text-[#F4F8F9] text-lg leading-relaxed">
                {s.value}
              </p>
            </li>
          );
        })}
      </ul>

      <p
        data-testid="atmosic-signature"
        className="mt-12 font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/75 text-center"
      >
        Witnessed continuously · not on a quarterly press release
      </p>
    </div>
  </section>
);

export default AtmosicIoT;
